import { Mail, MessageSquare, PhoneCall, Waves, LayoutDashboard, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Reveal, SectionHeading } from '../components/Reveal';
import { Waveform, MiniAnalytics } from '../components/mockui';

const cardCls =
  'group relative h-full overflow-hidden rounded-3xl border border-brand-line bg-white p-7 shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lift';

export const Features = () => (
  <section className="py-20 lg:py-28" data-testid="features-section">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <SectionHeading
          eyebrow="Why CapableBee"
          title="Smarter Workflow, Faster Results"
          sub="Your AI assistant Bee that generates leads like a pro."
        />
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-12">
        <Reveal className="lg:col-span-7" data-testid="feature-card-call-handling">
          <div className={cardCls}>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep">
                <PhoneCall className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-bold tracking-tight">Instant Call Handling</h3>
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-600">
              Respond to every caller instantly. AI handles multiple calls at once without wait times or missed
              opportunities.
            </p>
            <div className="mt-6 rounded-2xl border border-brand-lineBlue/60 bg-brand-iceSoft p-4">
              <div className="flex items-center justify-between text-xs font-bold text-brand-navy">
                <span>Incoming call · AI answering</span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                  Answering now
                </span>
              </div>
              <Waveform bars={26} className="mt-4 h-10" />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.08} className="lg:col-span-5" data-testid="feature-card-human-voice">
          <div className={cardCls}>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep">
                <Waves className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-bold tracking-tight">Human-Like Voice</h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Your customers won't even realize they're talking to AI.
            </p>
            <div className="mt-6 flex h-24 items-center justify-center rounded-2xl bg-gradient-to-b from-brand-ice to-white">
              <Waveform bars={18} className="h-14" tone="bg-brand-sky" />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05} className="lg:col-span-4" data-testid="feature-card-follow-ups">
          <div className={cardCls}>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep">
                <Mail className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-bold tracking-tight">Automated Follow-Ups</h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Stay connected with every lead. Automatically send follow-ups through email, SMS, or calls after every
              conversation.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                { icon: <Mail className="h-3.5 w-3.5" />, label: 'Email' },
                { icon: <MessageSquare className="h-3.5 w-3.5" />, label: 'SMS' },
                { icon: <PhoneCall className="h-3.5 w-3.5" />, label: 'Call' },
              ].map((c) => (
                <span
                  key={c.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand-lineBlue bg-brand-iceSoft px-3 py-1.5 text-xs font-bold text-brand-skyDeep"
                >
                  {c.icon}
                  {c.label}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-4" data-testid="feature-card-dashboard">
          <div className={cardCls}>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep">
                <LayoutDashboard className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-bold tracking-tight">Custom Dashboard</h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Customize the dashboard display according to your needs.
            </p>
            <MiniAnalytics className="mt-6" />
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-4" data-testid="feature-card-scalable">
          <div className={`${cardCls} bg-gradient-to-b from-brand-navy to-brand-panel text-white`}>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-brand-honey">
                <Users className="h-5 w-5" />
              </span>
              <h3 className="text-xl font-bold tracking-tight text-white">Scalable for Any Business</h3>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/75">
              CapableBee grows with your business and manages conversations effortlessly.
            </p>
            <Link
              to="/contact"
              data-testid="feature-scalable-cta"
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-honey transition-colors hover:text-white"
            >
              Get Started
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <TrendingUp className="pointer-events-none absolute -bottom-6 -right-6 h-32 w-32 text-white/5" aria-hidden="true" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
